import React, { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { StyledAppLayout } from "./styles";
import { Header } from "../../components/Header";
import { Footer } from "../../components/Footer";
import { ContactUsSection } from "../../components/landing/ContactUsSection";
import {
  CHROME_STORE_EXTENSION_URL,
  HOMEPAGE_ANCHOR_CONTAINER_IDs,
} from "../../lib/constants";
import { waitForElement } from "../../lib/utils";
import SuggestionPopup from "../../components/SuggestionPopup";
import SEO from "../../components/SEO";
import { AppContext } from "../../contexts/AppContext";

export default function AppLayout({
  children,
  afterContactUs,
  hideContactUsSection = false,
  noNavigationMenu = false,
  mainNavigationButton,
  pageConfig,
  topBanner,
  footerDescription,
}) {
  // OUT-949: isExtensionInstalled comes from AppContext (driven by useExtensionDeeplinkHandoff in
  // App.jsx) so the "extension not installed" popup works regardless of which page rendered.
  const { isExtensionInstalled } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentPathname = location.pathname;
    const currentHash = location.hash;
    const currentSearch = location.search;

    const clearHashFromUrl = (delay = 500) => {
      setTimeout(() => {
        try {
          navigate(currentPathname + currentSearch, { replace: true });
        } catch {
          //
        }
      }, delay);
    };

    const processScrollBasedOnHash = async () => {
      if (!currentHash) {
        return;
      }

      const elementId = currentHash.replace(/^[#]sc[-]/, "");

      if (
        elementId === HOMEPAGE_ANCHOR_CONTAINER_IDs.aboutReach.key ||
        elementId === HOMEPAGE_ANCHOR_CONTAINER_IDs.hero.key
      ) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        clearHashFromUrl();
        return;
      }

      if (elementId) {
        try {
          const section = await waitForElement(elementId);
          if (section) {
            section.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
            clearHashFromUrl();
            return;
          }
        } catch {
          //
        }
      }

      // Fallback: If hash existed but was not processed above
      clearHashFromUrl(50);
    };

    processScrollBasedOnHash();
  }, [location, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  return (
    <StyledAppLayout $hasTopBanner={!!topBanner}>
      <SEO pageConfig={pageConfig} />
      {isExtensionInstalled === false && (
        <SuggestionPopup
          characterEmotion="sad"
          title={"We can’t find the Reach extension"}
          description={PopupDescriptionText()}
          styles={{
            titleStyle: {
              fontColor: "#ff0000",
            },
          }}
        />
      )}

      <Header
        noNavigationMenu={noNavigationMenu}
        mainButton={mainNavigationButton}
      />
      {topBanner}
      <main>
        {children}
        {!hideContactUsSection && <ContactUsSection />}
        {afterContactUs}
      </main>
      <Footer description={footerDescription} />
    </StyledAppLayout>
  );
}

const PopupDescriptionText = () => {
  return (
    <>
      It looks like you clicked a link that should open in Reach but Reach isn’t
      installed in this browser profile. Please switch profiles or reinstall
      Reach by clicking <a href={CHROME_STORE_EXTENSION_URL}>here</a>
    </>
  );
};
